import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { fetchTerrains, type TerrainView } from '../api/catalog';
import { ApiError, OfflineError } from '../api/http';
import { fetchServers, type ServerView } from '../api/servers';
import {
  abortSmokeNight,
  acceptUse,
  fetchLedger,
  fetchSmokeRuns,
  fetchTestEvidence,
  fetchUse,
  startSmokeNight,
  withdrawUse,
  type LedgerView,
  type SmokeRunView,
  type TestEvidence,
  type UseEntry,
  type UseView,
} from '../api/status';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { Shell } from './Shell';
import { fmtMoment } from './dates';

const TABS = ['smoke', 'ledger', 'use'] as const;
type Tab = (typeof TABS)[number];
const STATES = ['seen', 'people', 'failing', 'unverified', 'frozen'] as const;

/** A ledger state's chip: `people` (the owner's word, no session code) reads as neither seen nor unverified. */
const stateChip = (s: string) =>
  s === 'seen'
    ? 'ok'
    : s === 'failing'
      ? 'bad'
      : s === 'frozen'
        ? ''
        : s === 'people'
          ? 'people'
          : 'warn';

const when = (iso: string | null) => (iso ? fmtMoment(iso) : '');

function errorText(err: unknown, table: Record<string, string>): string {
  if (err instanceof OfflineError) return ca.errors.offline;
  // A stored mission the rig will not play (D11.12) says why in its own Catalan sentence.
  if (err instanceof ApiError && err.code.startsWith('smoke_mission_') && err.detail)
    return err.detail;
  if (err instanceof ApiError) return table[err.code] ?? ca.errors[err.code] ?? ca.errors.unknown;
  return ca.errors.unknown;
}

/**
 * The "Estat" page, an admin's instrument: what has been seen working and
 * what has not. Two views on one question — the smoke night the platform runs
 * by itself (VIABILITY_REVIEW.md §6, phase 1) and the verification ledger row
 * by row. The night's checklist that used to be the third tab is gone: the
 * guided tests on /proves replaced it, and the server keeps its items only
 * for the session page's timeline.
 */
export function Status() {
  const { me } = useAuth();
  // An admin's instrument and nobody else's: its endpoints answer 403 to a member, so a
  // member who opens the address by hand is sent home instead of reading a page of errors.
  if (!me.roles.includes('ADMIN')) return <Navigate to="/" replace />;
  return <StatusPage />;
}

function StatusPage() {
  const t = ca.statusPage;
  const { me } = useAuth();
  const [search, setSearch] = useSearchParams();
  const tabParam = search.get('tab');
  const tab: Tab = (TABS as readonly string[]).includes(tabParam ?? '')
    ? (tabParam as Tab)
    : 'smoke';
  const [error, setError] = useState<string | null>(null);

  return (
    <Shell title={t.title} width="wide">
      <p className="muted">{t.intro}</p>
      <nav className="tabs" role="tablist" aria-label={t.title}>
        {TABS.map((k) => (
          <button
            key={k}
            type="button"
            role="tab"
            aria-selected={tab === k}
            className={tab === k ? 'active' : ''}
            onClick={() => setSearch(k === 'smoke' ? {} : { tab: k }, { replace: true })}
          >
            {t.tabs[k]}
          </button>
        ))}
      </nav>
      {error && (
        <p className="notice bad" role="alert">
          {error}
        </p>
      )}
      {tab === 'smoke' && <SmokeTab isAdmin={me.roles.includes('ADMIN')} onError={setError} />}
      {tab === 'ledger' && <LedgerTab onError={setError} />}
      {tab === 'use' && <UseTab onError={setError} />}
    </Shell>
  );
}

function SmokeTab({ isAdmin, onError }: { isAdmin: boolean; onError: (e: string | null) => void }) {
  const t = ca.statusPage.smoke;
  const [runs, setRuns] = useState<SmokeRunView[] | null>(null);
  const [servers, setServers] = useState<ServerView[]>([]);
  const [terrains, setTerrains] = useState<TerrainView[]>([]);
  const [server, setServer] = useState<number | ''>('');
  const [world, setWorld] = useState('');
  const [candidate, setCandidate] = useState('');
  const [commit, setCommit] = useState('');
  const [stored, setStored] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchSmokeRuns()
      .then((r) => {
        setRuns(r);
        onError(null);
      })
      .catch((e) => {
        setRuns([]);
        onError(errorText(e, t.errors));
      });
  }, [onError, t.errors]);

  useEffect(() => {
    load();
  }, [load]);

  // A run in progress, or waiting its turn, refreshes itself every fifteen seconds.
  const running = runs?.some((r) => r.result === 'RUNNING' || r.result === 'QUEUED') ?? false;
  useEffect(() => {
    if (!running) return;
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [running, load]);

  useEffect(() => {
    if (!isAdmin) return;
    fetchServers()
      .then((r) => {
        const managed = r.servers.filter((s) => s.access === 'MANAGED');
        setServers(managed);
        if (managed.length > 0) setServer((cur) => (cur === '' ? managed[0].id : cur));
      })
      .catch(() => setServers([]));
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    // The terrains the night's own server can load: its mods are the mod set.
    fetchTerrains(server === '' ? undefined : server)
      .then((list) => {
        setTerrains(list);
        if (list.length > 0) setWorld((cur) => (cur === '' ? list[0].worldName : cur));
      })
      .catch(() => setTerrains([]));
  }, [isAdmin, server]);

  // A stored mission to play instead of generating one (D11.12): "code" or "code@revision".
  const [missionCode, revisionText] = stored.trim().split('@');
  const revision = revisionText === undefined ? null : Number(revisionText);
  const storedBad = revision !== null && (!Number.isInteger(revision) || revision < 1);

  const start = async () => {
    if (server === '' || (!world && !missionCode) || storedBad) return;
    setBusy(true);
    try {
      await startSmokeNight(
        missionCode
          ? {
              panelServerId: server,
              missionCode,
              revision,
              candidateMod: candidate || null,
              commitSha: commit || null,
            }
          : {
              panelServerId: server,
              worldClass: world,
              candidateMod: candidate || null,
              commitSha: commit || null,
            },
      );
      setNotice(t.started);
      onError(null);
      load();
    } catch (e) {
      onError(errorText(e, t.errors));
    } finally {
      setBusy(false);
    }
  };

  const last = runs && runs.length > 0 ? runs[0] : null;
  return (
    <div className="status-smoke">
      <p className="muted small">{t.intro}</p>
      {notice && (
        <p className="notice ok" role="status">
          {notice}
        </p>
      )}
      {isAdmin && (
        <section className="card" aria-label={t.start}>
          <h2>{t.start}</h2>
          {servers.length === 0 ? (
            <p className="muted small">{t.form.noServer}</p>
          ) : (
            <form
              className="form"
              onSubmit={(e) => {
                e.preventDefault();
                void start();
              }}
            >
              <label>
                {t.form.server}
                <select value={server} onChange={(e) => setServer(Number(e.target.value))}>
                  {servers.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t.form.mission}
                <input
                  value={stored}
                  onChange={(e) => setStored(e.target.value)}
                  placeholder="k7f2m9q@3"
                />
              </label>
              <p className={storedBad ? 'warn small' : 'muted small'}>
                {storedBad ? t.form.missionBad : t.form.missionHint}
              </p>
              {!missionCode && (
                <label>
                  {t.form.world}
                  <select value={world} onChange={(e) => setWorld(e.target.value)}>
                    {terrains.map((w) => (
                      <option key={w.worldName} value={w.worldName}>
                        {w.displayName} ({w.worldName})
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <label>
                {t.form.candidate}
                <input
                  value={candidate}
                  onChange={(e) => setCandidate(e.target.value)}
                  placeholder="@ops_rc"
                />
              </label>
              <label>
                {t.form.commit}
                <input value={commit} onChange={(e) => setCommit(e.target.value)} />
              </label>
              <button
                type="submit"
                className="primary"
                disabled={busy || server === '' || (!world && !missionCode) || storedBad}
              >
                {busy ? t.starting : t.start}
              </button>
            </form>
          )}
        </section>
      )}
      <section className="card" aria-label={t.last}>
        <h2>{t.last}</h2>
        {runs === null && <p className="muted small">…</p>}
        {runs !== null && !last && <p className="muted small">{t.none}</p>}
        {last && (
          <RunCard
            run={last}
            isAdmin={isAdmin}
            onAbort={() =>
              abortSmokeNight(last.id)
                .then(load)
                .catch((e) => onError(errorText(e, t.errors)))
            }
          />
        )}
      </section>
      {runs && runs.length > 1 && (
        <section className="card" aria-label={t.history}>
          <h2>{t.history}</h2>
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Id</th>
                  <th>{t.session}</th>
                  <th>Versió</th>
                  <th>Resultat</th>
                </tr>
              </thead>
              <tbody>
                {runs.slice(1).map((r) => (
                  <tr key={r.id}>
                    <td className="num">{r.id}</td>
                    <td>
                      {r.sessionCode ? (
                        <Link to={`/s/${r.sessionCode}`}>{r.sessionCode}</Link>
                      ) : (
                        '—'
                      )}{' '}
                      <span className="muted small">{when(r.startedAt)}</span>
                    </td>
                    <td className="muted small">{t.version(r.platformVersion, r.addonVersion)}</td>
                    <td>
                      <span className={`chip ${chipOf(r.result)}`}>{t.result(r.result)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function chipOf(result: SmokeRunView['result']): string {
  return result === 'GREEN' ? 'ok' : result === 'RED' ? 'bad' : 'warn';
}

function RunCard({
  run,
  isAdmin,
  onAbort,
}: {
  run: SmokeRunView;
  isAdmin: boolean;
  onAbort: () => void;
}) {
  const t = ca.statusPage.smoke;
  return (
    <div className="smoke-run">
      <p>
        <span className={`chip ${chipOf(run.result)}`}>{t.result(run.result)}</span>{' '}
        {run.result === 'RUNNING' && <span className="muted">{t.phase(run.phase)}</span>}
        {run.result === 'QUEUED' && (
          <span className="muted">{t.queued(run.position, run.waiting)}</span>
        )}{' '}
        <span className="muted small">
          {when(run.startedAt)} · {run.worldClass} · {run.recipe} ·{' '}
          {t.version(run.platformVersion, run.addonVersion)}
          {run.candidateMod ? ` · ${t.candidate(run.candidateMod)}` : ''}
          {run.commitSha ? ` · ${t.commit(run.commitSha)}` : ''}
        </span>
      </p>
      {run.sessionCode && (
        <p className="small">
          {t.session}: <Link to={`/s/${run.sessionCode}`}>{run.sessionCode}</Link>
        </p>
      )}
      {run.note && (
        <p className="small">
          {t.note}: {run.note}
        </p>
      )}
      {(run.result === 'RUNNING' || run.result === 'QUEUED') && isAdmin && (
        <button type="button" className="danger" onClick={onAbort}>
          {t.abort}
        </button>
      )}
      {run.checks.length > 0 && (
        <>
          <h3>{t.checks}</h3>
          <ul className="checks">
            {run.checks.map((c) => (
              <li key={c.id} className={c.status}>
                <span
                  className={`chip ${c.status === 'pass' ? 'ok' : c.status === 'fail' ? 'bad' : c.status === 'warn' ? 'warn' : ''}`}
                >
                  {t.check(c.status)}
                </span>{' '}
                <strong>{c.title}</strong> <span className="muted small">{c.ledger}</span>
                {c.evidence.length > 0 && (
                  <ul className="evidence">
                    {c.evidence.map((line, i) => (
                      <li key={i}>
                        <code>{line}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function LedgerTab({ onError }: { onError: (e: string | null) => void }) {
  const t = ca.statusPage.ledger;
  const [ledger, setLedger] = useState<LedgerView | null>(null);
  const [filter, setFilter] = useState<string>('all');
  // What the sprint's guided tests answered per row: the live count beside the text the jar carries.
  const [evidence, setEvidence] = useState<Record<string, TestEvidence>>({});
  useEffect(() => {
    fetchLedger()
      .then((l) => {
        setLedger(l);
        onError(null);
      })
      .catch((e) => onError(errorText(e, {})));
    fetchTestEvidence()
      .then(setEvidence)
      .catch(() => setEvidence({}));
  }, [onError]);
  if (!ledger) return <p className="muted small">…</p>;
  return (
    <div className="status-ledger">
      <p className="muted small">{t.intro}</p>
      <p className="row">
        {ledger.builtOn && <span className="muted small">{t.builtOn(ledger.builtOn)}</span>}
        <span className="chips">
          <button
            type="button"
            className={`chip ${filter === 'all' ? 'on' : ''}`}
            onClick={() => setFilter('all')}
          >
            {t.all}
          </button>
          {STATES.map((s) => (
            <button
              key={s}
              type="button"
              className={`chip ${stateChip(s)} ${filter === s ? 'on' : ''}`}
              onClick={() => setFilter(s)}
            >
              {t.states[s]} · {ledger.counts[s] ?? 0}
            </button>
          ))}
        </span>
      </p>
      {ledger.sections.map((section) => {
        const rows = section.rows.filter((r) => filter === 'all' || r.state === filter);
        if (rows.length === 0) return null;
        return (
          <section key={section.key} className="card" aria-label={section.title}>
            <h2>
              {section.key}. {section.title}
            </h2>
            <div className="table-wrap">
              <table className="data ledger">
                <thead>
                  <tr>
                    <th>{t.columns.id}</th>
                    <th>{t.columns.row}</th>
                    <th>{t.columns.check}</th>
                    <th>{t.columns.status}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} className={r.state}>
                      <td className="num">{r.id}</td>
                      <td>
                        {r.text}
                        {evidence[r.id] && (
                          <>
                            {' '}
                            <span className="chip" title={ca.ledgerEvidence.title}>
                              {ca.ledgerEvidence.label(
                                evidence[r.id].yes,
                                evidence[r.id].no,
                                evidence[r.id].cant,
                              )}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="muted small">{r.check}</td>
                      <td>
                        <span className={`chip ${stateChip(r.state)}`}>{t.states[r.state]}</span>{' '}
                        <span className="small">{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

const day = (iso: string) => new Date(iso).toLocaleDateString('ca', { dateStyle: 'medium' });

/**
 * «Vist en ús»: what real people have done with the platform since the
 * running version started, counted from the activity log, and the
 * administrator's own word on each. The platform never decides and the page
 * never says who: numbers, and «tu» for his own use.
 */
function UseTab({ onError }: { onError: (e: string | null) => void }) {
  const t = ca.statusPage.use;
  const [view, setView] = useState<UseView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  useEffect(() => {
    fetchUse()
      .then((v) => {
        setView(v);
        onError(null);
      })
      .catch((e) => onError(errorText(e, { unknown: t.error })));
  }, [onError, t.error]);
  if (!view) return <p className="muted small">…</p>;

  const decide = (entry: UseEntry, accept: boolean) => {
    setBusy(entry.id);
    (accept ? acceptUse(entry.id) : withdrawUse(entry.id))
      .then((now) => {
        setView((v) => v && { ...v, entries: v.entries.map((e) => (e.id === now.id ? now : e)) });
        onError(null);
      })
      .catch((e) => onError(errorText(e, {})))
      .finally(() => setBusy(null));
  };

  // Nobody yet, himself included, and no word given: out of the way until somebody does.
  const nobody = (e: UseEntry) => e.people === 0 && !e.mine && !e.acceptance;
  const used = view.entries.filter((e) => !nobody(e));
  const unused = view.entries.filter(nobody);

  const row = (e: UseEntry) => {
    const standing = e.acceptance && !e.acceptance.stale;
    return (
      <li key={e.id}>
        <div className="use-what">
          <p>{e.label}</p>
          <p className="small">
            {e.people === 0 && e.mine ? (
              t.mineOnly
            ) : (
              <>
                {t.people(e.people, view.version)}
                {e.mine && !e.adminAction && <> · {t.mine}</>}
              </>
            )}
            {e.peopleEver > e.people && <span className="muted"> · {t.ever(e.peopleEver)}</span>}
          </p>
          {e.breakdown.length > 0 && (
            <p className="chips">
              {e.breakdown.map((p) => (
                <span key={p.label} className="chip">
                  {p.label} · {p.people}
                </span>
              ))}
            </p>
          )}
          <p className="muted small use-refs">
            {e.ledgerRow && <span>{t.ledgerRow(e.ledgerRow)}</span>}
            {e.roadmapItem && (
              <span>
                {t.roadmapItem}: <Link to={`/full-de-ruta#${e.roadmapItem}`}>{e.roadmapItem}</Link>
              </span>
            )}
          </p>
        </div>
        <div className="use-word">
          {e.acceptance && standing && (
            <span className="chip ok" title={e.acceptance.note ?? undefined}>
              <i className="bi bi-check-lg" aria-hidden="true" />{' '}
              {t.accepted(day(e.acceptance.acceptedAt))}
            </span>
          )}
          {e.acceptance && !standing && (
            <span className="chip warn" title={t.acceptedCount(e.acceptance.count)}>
              <i className="bi bi-clock-history" aria-hidden="true" />{' '}
              {t.stale(e.acceptance.version)}
            </span>
          )}
          {!standing && (
            <button
              type="button"
              className={e.people >= 1 ? 'primary' : ''}
              disabled={busy === e.id}
              onClick={() => decide(e, true)}
            >
              {t.accept}
            </button>
          )}
          {e.acceptance && (
            <button
              type="button"
              className="link"
              disabled={busy === e.id}
              onClick={() => decide(e, false)}
            >
              {t.undo}
            </button>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className="status-use">
      <p className="muted small">{t.help}</p>
      <p className="muted small">{t.since(view.version, day(view.since))}</p>
      <section className="card" aria-label={ca.statusPage.tabs.use}>
        {used.length === 0 ? (
          <p className="muted small">{t.empty}</p>
        ) : (
          <ul className="use-list">{used.map(row)}</ul>
        )}
      </section>
      {unused.length > 0 && (
        <details className="card use-none">
          <summary>{t.none(unused.length)}</summary>
          <ul className="use-list">{unused.map(row)}</ul>
        </details>
      )}
    </div>
  );
}
